import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";

const blogPosts = [
  {
    title: "5 Ways to Keep Your Court Reporter Happy During Depositions",
    excerpt: "Small courtesies make a big difference. Learn how to ensure smooth proceedings and accurate transcripts.",
    category: "Tips & Tricks",
    readTime: "4 min read",
    slug: "#"
  },
  {
    title: "Court Reporting in Action: A Day in Federal Court",
    excerpt: "Follow along as we capture a high-profile trial from setup to final transcript delivery.",
    category: "Behind the Scenes",
    readTime: "6 min read",
    slug: "#"
  },
  {
    title: "The Art of Speaking Clearly: A Guide for Attorneys",
    excerpt: "Why your speaking habits matter for the record and how to improve deposition outcomes.",
    category: "For Attorneys",
    readTime: "5 min read",
    slug: "#"
  }
];

export default function Footer() {
  return (
    <footer className="bg-[#0D0D0D] border-t border-[#333333]" data-testid="footer">
      {/* Blog Section */}
      <section className="py-20 md:py-24 border-b border-[#333333] relative overflow-hidden">
        <div className="absolute top-[10%] right-[5%] w-[600px] h-[500px] bg-[#38b6ff]/16 rounded-full blur-[200px]"></div>
        <div className="absolute bottom-[15%] left-[0%] w-[500px] h-[400px] bg-[#1a5aaa]/12 rounded-full blur-[170px]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <p 
                className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-4"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                From the Blog
              </p>
              <h2 
                className="text-2xl sm:text-3xl font-bold tracking-tight text-[#F5F5F7]"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                Insights & <span className="gradient-text">Resources</span>
              </h2>
            </div>
            <Link 
              to="/blog"
              className="mt-4 md:mt-0 inline-flex items-center gap-2 text-[#38b6ff] hover:text-[#E8E8E8] transition-colors text-sm"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              View All Posts
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogPosts.map((post, index) => (
              <article 
                key={index}
                className="glass-card p-6 group cursor-pointer"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span 
                    className="text-xs px-2 py-1 border border-[#38b6ff]/30 text-[#38b6ff]"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {post.category}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-[#B0B0B0]">
                    <Clock className="w-3 h-3" />
                    {post.readTime}
                  </span>
                </div>
                <h3 
                  className="text-lg font-bold text-[#F5F5F7] mb-3 group-hover:text-[#38b6ff] transition-colors leading-tight"
                  style={{ fontFamily: 'Unbounded, sans-serif' }}
                >
                  {post.title}
                </h3>
                <p className="text-[#B0B0B0] text-sm leading-relaxed mb-4">
                  {post.excerpt}
                </p>
                <span 
                  className="inline-flex items-center gap-1 text-xs text-[#E8E8E8] group-hover:gap-2 transition-all"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  Read More
                  <ArrowRight className="w-3 h-3" />
                </span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Footer Grid */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            {/* Logo and Description */}
            <div className="md:col-span-4">
              <Link to="/" className="inline-block mb-6">
                <span 
                  className="text-xl font-bold text-[#F5F5F7] tracking-tight"
                  style={{ fontFamily: 'Unbounded, sans-serif' }}
                >
                  Aym High <span className="text-[#38b6ff]">Steno</span>
                </span>
              </Link>
              <p className="text-[#E0E0E0] text-sm leading-relaxed max-w-sm">
                Aym High Steno provides professional court reporting services and 
                comprehensive training programs for aspiring legal professionals.
              </p>
            </div>

            {/* Services */}
            <div className="md:col-span-2">
              <h4 
                className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8E8E8] mb-6"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                Services
              </h4>
              <nav className="flex flex-col space-y-3">
                <Link to="/hire" className="text-[#E0E0E0] hover:text-[#38b6ff] active:text-[#38b6ff] text-sm transition-colors py-1">
                  Hire a Reporter
                </Link>
                <Link to="/hire#book" className="text-[#E0E0E0] hover:text-[#38b6ff] active:text-[#38b6ff] text-sm transition-colors py-1">
                  Request Quote
                </Link>
              </nav>
            </div>

            {/* Programs */}
            <div className="md:col-span-2">
              <h4 
                className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-6"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                Programs
              </h4>
              <nav className="flex flex-col space-y-3">
                <Link to="/stenou#stenou" className="text-[#E0E0E0] hover:text-[#38b6ff] active:text-[#38b6ff] text-sm transition-colors py-1">
                  StenoU
                </Link>
                <Link to="/stenou#editu" className="text-[#E0E0E0] hover:text-[#38b6ff] active:text-[#38b6ff] text-sm transition-colors py-1">
                  EditU
                </Link>
                <Link to="/stenou#proofu" className="text-[#E0E0E0] hover:text-[#38b6ff] active:text-[#38b6ff] text-sm transition-colors py-1">
                  ProofU
                </Link>
              </nav>
            </div>

            {/* Quick Links */}
            <div className="md:col-span-2">
              <h4 
                className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-6"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                Company
              </h4>
              <nav className="flex flex-col space-y-3">
                <Link to="/" className="text-[#E0E0E0] hover:text-[#38b6ff] active:text-[#38b6ff] text-sm transition-colors py-1">
                  Home
                </Link>
                <Link to="/about" className="text-[#E0E0E0] hover:text-[#38b6ff] active:text-[#38b6ff] text-sm transition-colors py-1">
                  About
                </Link>
                <Link to="/faq" className="text-[#E0E0E0] hover:text-[#38b6ff] active:text-[#38b6ff] text-sm transition-colors py-1">
                  FAQ
                </Link>
                <Link to="/login" className="text-[#E0E0E0] hover:text-[#38b6ff] active:text-[#38b6ff] text-sm transition-colors py-1">
                  Login
                </Link>
              </nav>
            </div>

            {/* Get Started */}
            <div className="md:col-span-2">
              <h4 
                className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8E8E8] mb-6"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                Get Started
              </h4>
              <Link
                to="/stenou#consultation"
                className="inline-block text-[#38b6ff] hover:text-[#E8E8E8] font-medium transition-colors text-sm"
              >
                Book Consultation →
              </Link>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-16 pt-8 border-t border-[#333333] flex flex-col md:flex-row items-center justify-between">
            <p className="text-[#E0E0E0] text-xs" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              &copy; {new Date().getFullYear()} Aym High Steno. All rights reserved.
            </p>
            <div className="flex items-center gap-4 mt-4 md:mt-0">
              <span className="text-[#E0E0E0] text-xs" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Programs:
              </span>
              <span className="text-[#38b6ff] text-xs">StenoU</span>
              <span className="text-[#E8E8E8] text-xs">EditU</span>
              <span className="text-[#38b6ff] text-xs">ProofU</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
